/**
 * Frontend Discipline Plugin System - Types
 *
 * Mirrors the backend plugin system structure for consistency.
 * Each discipline can provide custom UI components, validation, and behavior.
 */

// =============================================================================
// ENUMS - Aligned with backend ParticipantType
// =============================================================================

export enum ParticipantType {
  INDIVIDUAL = 'individual',
  PAIR = 'pair',
  TEAM = 'team'
}

export enum CompetitionTypeStatus {
  AVAILABLE = 'available',
  BETA = 'beta',
  PLANNED = 'planned',
  EXPERIMENTAL = 'experimental',
  DEPRECATED = 'deprecated'
}

export enum HierarchyLevel {
  CIRCOLO = 'circolo',
  PROVINCIAL = 'provincial',
  REGIONAL = 'regional',
  NATIONAL = 'national',
  INTERNATIONAL = 'international'
}

// =============================================================================
// CONFIGURATION TYPES
// =============================================================================

export interface CompetitionTypeConfig {
  code: string;
  name: string;
  description: string;
  participant_type: ParticipantType;
  min_participants: number;
  max_participants?: number;
  status: CompetitionTypeStatus;
  scoring_systems: string[];
  default_scoring_system?: string;
  requires_boards?: boolean;
  boards_per_round?: number;
  default_rounds?: number;
  movement_types?: string[];
  features: Record<string, unknown>;
  is_team_event?: boolean;
  team_size?: number;
  icon?: string;
}

/**
 * Tipo di composizione partecipante ('tipo di squadra') di una disciplina.
 * Definisce DA CHI è formata l'unità che gareggia (composed_of): es. Bridge
 * 'team_of_pairs' è composto da coppie (PAIR); una coppia è composta da individui.
 * Single-source-of-truth del backend (plugin.get_squad_types()).
 */
export interface SquadTypeConfig {
  code: string;
  name: string;
  description?: string;
  participant_type: ParticipantType;
  composed_of: ParticipantType;
  players_per_unit: number;
  min_units: number;
  max_units?: number;
  min_players?: number;
  max_players?: number;
  is_default?: boolean;
  icon?: string;
  /** Etichetta singolare per l'unità partecipante (es. "Coppia", "Squadra", "Giocatore"). */
  unitLabelSingular?: string;
  /** Etichetta plurale per l'unità partecipante (es. "Coppie", "Squadre", "Giocatori"). */
  unitLabelPlural?: string;
}

export interface StaffRoleConfig {
  code: string;
  name: string;
  name_plural: string;
  description: string;
  is_required: boolean;
  min_per_competition: number;
  max_per_competition?: number;
  can_be_player: boolean;
  icon?: string;
}

export interface MembershipCategoryConfig {
  code: string;
  name: string;
  description: string;
  is_official: boolean;
  earns_points: boolean;
  age_min?: number;
  age_max?: number;
  icon?: string;
}

export interface PlayerCategoryGroupConfig {
  code: string;
  name: string;
  description: string;
  category_codes: string[];
  display_order: number;
  icon?: string;
}

export interface CompetitionLevelConfig {
  code: string;
  name: string;
  description: string;
  hierarchy_level: HierarchyLevel;
  points_multiplier: string;
  requires_homologation: boolean;
  icon?: string;
}

export interface ScoringSystemConfig {
  code: string;
  name: string;
  description: string;
  icon?: string;
  // Extended fields from backend plugin
  scoringType?: string;        // Type: points, percentage, imp, elo, sets, etc.
  isHigherBetter?: boolean;    // True = higher score is better
  formatTemplate?: string;     // Display format (e.g., "{value}%", "{value} MP")
  displayOrder?: number;       // Order in UI dropdowns
}

export interface RankingSystemConfig {
  code: string;
  name: string;
  description: string;
  method: string;
  pointsPerWin: string;
  pointsPerDraw: string;
  pointsPerLoss: string;
  initialRating: string;
  kFactor: string;
  minGamesForRanking: number;
  icon?: string;
}

export interface PairingStrategyConfig {
  code: string;
  name: string;
  description: string;
  firstDayStrategy: string;
  subsequentDaysStrategy: string;
  useHistoricalRanking: boolean;
  skillBalanceTolerance: string;
  avoidRepeatPairings: boolean;
  repeatPairingCooldownDays: number;
  icon?: string;
}

/**
 * Movement type for a discipline (e.g. Bridge mitchell/howell variants).
 * Single-source-of-truth: backend plugin.get_movement_types().
 * Bridge returns 6 items; other disciplines return [].
 */
export interface MovementTypeConfig {
  code: string;
  name: string;
  description: string;
  applicableCompetitionTypes: string[];
  minTables: number;
  maxTables?: number;
  supportsOdd: boolean;
  isDefault: boolean;
  icon?: string;
  features: Record<string, unknown>;
}

export interface CompetitionFormatConfig {
  code: string;
  name: string;
  description: string;
  formatType: string;
  hasSeasons: boolean;
  dynamicPairing: boolean;
  minDaysPerSeason: number;
  maxDaysPerSeason?: number;
  rankingCarryover: boolean;
  carryoverPercentage: string;
  icon?: string;
}

// =============================================================================
// PLUGIN METADATA
// =============================================================================

export interface DisciplinePluginMetadata {
  code: string;
  name: string;
  version: string;
  description: string;
  icon?: string;
  color?: string;  // Theme color for UI
  /**
   * «Questa disciplina ha un gioco al tavolo in SportStack» (§5.7 di
   * `funzionalita-nel-plugin.md`): lo sa il BACKEND — dipende dai router
   * che il plugin dichiara (`get_routers()`) — e lo dice il backend, nel
   * DTO di `/api/sport-disciplines/with-implementation`. Mai dedotto qui.
   */
  playSurface?: boolean;
  /** La frase da mostrare quando il gioco al tavolo non c'è, decisa dal backend. */
  playSurfaceLabel?: string | null;
}

// =============================================================================
// VALIDATION TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  code: string;
}

// =============================================================================
// COMPETITION DETAIL PAGE CONFIGURATION
// =============================================================================

/** Navigation card definition for competition detail page */
export interface NavigationCardConfig {
  id: string;           // Unique ID (e.g., "participants", "matches", "bookings")
  icon: string;         // Emoji or icon string
  label: string;        // Card title
  description: string;  // Subtitle
  href: string;         // Relative route suffix (e.g., "/participants")
  // 'finance': il riquadro si vede quando il backend dice `can_view_finance`
  visibleTo: 'all' | 'organizer' | 'finance';  // Who sees it
  order: number;        // Display order
}

/** Booking terminology per discipline */
export interface BookingTerminology {
  sectionTitle: string;           // "Prenotazioni" / "Iscrizioni"
  totalLabel: string;             // "Prenotazioni Totali" / "Iscrizioni Totali"
  confirmedLabel: string;         // "Confermate" / "Confermati"
  pendingLabel: string;           // "In Attesa"
  singleAvailableLabel: string;   // "Singoli Disponibili" / "Giocatori Singoli"
  infoTitle: string;              // "Informazioni Prenotazioni" / "Informazioni Iscrizioni"
  openLabel: string;              // "Apertura Prenotazioni" / "Apertura Iscrizioni"
  closeLabel: string;             // "Chiusura Prenotazioni" / "Chiusura Iscrizioni"
  notesLabel: string;             // "Note Prenotazioni" / "Note Iscrizioni"
}

/** Competition detail page configuration from plugin */
export interface CompetitionDetailConfig {
  // Terminology
  terminology: {
    competitionSingular: string;   // "Torneo" / "Competizione" / "Incontro"
    competitionPlural: string;     // "Tornei" / "Competizioni" / "Incontri"
    participantSingular: string;   // "Coppia" / "Partecipante" / "Giocatore"
    participantPlural: string;     // "Coppie" / "Partecipanti" / "Giocatori"
    matchSingular: string;         // "Turno" / "Partita" / "Match"
    matchPlural: string;           // "Turni" / "Partite" / "Match"
    resultLabel: string;           // "Classifica" / "Risultati"
  };
  // Booking terminology
  bookingTerminology?: BookingTerminology;
  // Theme
  themeColor: string;              // Accent color (e.g., '#1e40af')
  disciplineIcon: string;          // Icon (e.g., '🃏', '⚽', '🏓')
  // Navigation cards override
  navigationCards?: NavigationCardConfig[];
  // Section visibility
  sections?: {
    showBookingInline?: boolean;   // Show booking section inline
    showStaff?: boolean;           // Show staff card (default: true)
    showManagers?: boolean;        // Show managers card (default: true)
    showPrizes?: boolean;          // Show prizes card (default: true)
    showParticipationFees?: boolean; // Show fees card (default: true)
  };
}

// =============================================================================
// DISCIPLINE PLUGIN INTERFACE
// =============================================================================

export interface DisciplinePlugin {
  /**
   * Plugin metadata
   */
  getMetadata(): DisciplinePluginMetadata;

  /**
   * Get all competition types for this discipline
   */
  getCompetitionTypes(): CompetitionTypeConfig[];

  /**
   * Get a specific competition type by code
   */
  getCompetitionType(code: string): CompetitionTypeConfig | undefined;

  /**
   * Get the participant composition types ('squad types') for this discipline.
   * Optional: implemented by the backend-synced adapter. Defines what each
   * competing unit is composed of (e.g. Bridge: pair + team-of-pairs).
   */
  getSquadTypes?(): SquadTypeConfig[];

  /**
   * Get staff roles for this discipline
   */
  getStaffRoles(): StaffRoleConfig[];

  /**
   * Get player categories for this discipline
   */
  getMembershipCategories(): MembershipCategoryConfig[];

  /**
   * Get player category groups for this discipline
   */
  getPlayerCategoryGroups(): PlayerCategoryGroupConfig[];

  /**
   * Get competition levels for this discipline
   */
  getCompetitionLevels(): CompetitionLevelConfig[];

  /**
   * Get scoring systems available for this discipline
   */
  getScoringSystems(): ScoringSystemConfig[];

  /**
   * Get ranking systems for this discipline (optional)
   */
  getRankingSystems?(): RankingSystemConfig[];

  /**
   * Get pairing strategies for this discipline (optional)
   */
  getPairingStrategies?(): PairingStrategyConfig[];

  /**
   * Get competition formats for this discipline (optional)
   */
  getCompetitionFormats?(): CompetitionFormatConfig[];

  /**
   * Get movement types for this discipline (optional).
   * Returns the plugin's movement catalog (e.g. Bridge mitchell/howell variants).
   * Backend is the single source of truth via plugin.get_movement_types().
   * Returns [] when unavailable or for non-bridge disciplines.
   */
  getMovementTypes?(): MovementTypeConfig[];

  /**
   * Validate competition configuration
   */
  validateCompetition(config: Record<string, unknown>): ValidationResult;

  /**
   * Get available competition types (excluding planned/deprecated)
   */
  getAvailableCompetitionTypes?(): CompetitionTypeConfig[];

  /**
   * Get Italian label for a competition type
   */
  getCompetitionTypeLabel(code: string): string;

  /**
   * Get Italian label for participant type
   */
  getParticipantTypeLabel(type: ParticipantType): string;

  // ==========================================================================
  // OPTIONAL CONFIGURATION
  // ==========================================================================

  /**
   * Get competition detail page configuration (terminology, nav cards, theme)
   */
  getCompetitionDetailConfig?(): CompetitionDetailConfig;

  // ⛔ DEC-P5 (`.claude/memory_bank/decisions/funzionalita-nel-plugin.md`
  // §8, decisa dall'utente il 2026-10-03): qui stavano cinque slot UI
  // dichiarati e mai letti (`CompetitionTypeForm`, `CompetitionTypeSelector`,
  // `ScoringSystemSelector`, `CompetitionPreview`, `customComponents`,
  // l'ultimo privo di tipi). Un meccanismo solo: le viste del gioco al
  // tavolo sono gli slot tipizzati di `plugins/disciplines/gameViews.ts`
  // (`GameViews`), risolti pigramente da `getGameViewsAsync` — non un
  // secondo elenco di componenti opzionali su questa interfaccia, che
  // arriva dal BACKEND (la configurazione) e non deve portare componenti
  // React (il bundle).
}

// =============================================================================
// PLUGIN REGISTRY TYPES
// =============================================================================

export interface PluginRegistry {
  register(plugin: DisciplinePlugin): void;
  get(code: string): DisciplinePlugin | undefined;
  getAll(): DisciplinePlugin[];
  getCodes(): string[];
  has(code: string): boolean;
}
